import { EventEmitter, Injectable } from '@angular/core';
import { AbstractChatService } from './chat.abstract.service';
import { ChatMessage, ChatMessageRatingEnum, ChatMetadata, EngagmentConversationQuestionData, ExternalConverdationalUseCaseData, ServerResMessageData } from './chat-data';
import { Observable, Subject } from 'rxjs';
import { APIS } from 'src/app/apis';
import { HttpClient } from '@angular/common/http';
import { EnvConfigService } from '../env-config/env-config.service';
import { EnvConfigKeys } from '../env-config/env-keys';
import { AbstractLocalStorageService } from '../localStorage/local-storage.abstract.service';
import { LocalStorageKeys } from '../localStorage/local-storage.enum';
import { ChatTypeEnum } from './chatEnum';

@Injectable({
  providedIn: 'root'
})
export class ChatService implements AbstractChatService{
  private liveSocket$: WebSocket | undefined = undefined;
  private liveSocketUrl: string = '';
  private setUserMessageUrl = `${APIS.chat}/getChatResponseForMessage`;
  private getChatsUrl = `${APIS.chat}/getChats`;
  private getStructuredProcessChatUrl = `${APIS.chat}/getStructuredProcessChat`;
  private deleteChatUrl = `${APIS.chat}/deleteChat`;
  private getChatMessagesUrl = `${APIS.chat}/getChatMessages`;
  private startNewChatUrl = `${APIS.chat}/startNewChat`;
  private startNewStructuredProcessChatUrl = `${APIS.chat}/startNewStructuredProcessChat`;
  private setMessageRatingUrl = `${APIS.chat}/setMessageRating`;
  private getConversationalQuestionUrl = `${APIS.chat}/getConversationalQuestion`;
  private getUseCasesToChatUrl = `${APIS.chat}/getUseCasesToChat`;
  private registerChatForAudioUrl = `${APIS.chat}/registerChatForAudio`;
  private unregisterChatsForAudioUrl = `${APIS.chat}/unregisterChatsForAudio`;
  private registerPeopleOfInterestUrl = `${APIS.chat}/registerPeopleOfInterest`;

  public liveChatConnectedEvent: EventEmitter<void> = new EventEmitter<void>();
  public liveChatDisconnectedEvent: EventEmitter<void> = new EventEmitter<void>();

  private messageSubject = new Subject<string>();

  isWebsocketConnected : boolean = false;
  private userInitiatedClose: boolean = false;
  private isReconnecting: boolean = false;

  constructor(private http: HttpClient,
              private envConfig : EnvConfigService,
              private localStorage: AbstractLocalStorageService) { 

      this.liveSocketUrl = this.envConfig.get(EnvConfigKeys.chatLLMBaseUrl)+APIS.chat+'/ws';
    }

  submitUserMessage(userMessage: string){

  }

  public liveChat_connect(chatId: string): void{
    // console.log(`chatSerivce:liveChat_connect chatId ${chatId}`);
    if (this.isReconnecting){
      this.isReconnecting = false;
    }
    // get auth token
    const authToken : string | undefined = this.localStorage.getSecured(LocalStorageKeys.moovToken);

    if (!authToken){
      return;
    }

    const liveChatSocketWIthTokenUrl = `${this.liveSocketUrl}?chatId=${chatId}&token=${authToken}`;
    
    this.liveSocket$ = new WebSocket(liveChatSocketWIthTokenUrl);

    this.liveSocket$.onopen = () => {
      // console.log(`chatService onOpen was triggered`);
      this.userInitiatedClose = false;
      this.isWebsocketConnected = true;
      // console.log('WebSocket connection established');
      this.liveChatConnectedEvent.emit();
    };
   
    this.liveSocket$.onmessage = (event) => {
      const message = event.data;
      this.messageSubject.next(message);  // Push received messages to the subject
    };

    this.liveSocket$.onclose = () => {
      // console.log('WebSocket connection closed');
      this.isWebsocketConnected = false;
      this.liveChatDisconnectedEvent.emit();
      if (!this.userInitiatedClose) {
        this.tryReconnect(chatId);  // Attempt to reconnect if not user-initiated
      }
    };

    this.liveSocket$.onerror = (error) => {
      // console.error('WebSocket error:', error);
      this.isWebsocketConnected = false;
      this.liveChatDisconnectedEvent.emit();
      if (!this.userInitiatedClose) {
        this.tryReconnect(chatId);  // Attempt to reconnect if not user-initiated
      }
    };    

  }

  private tryReconnect(chatId: string): void {
    if (!this.userInitiatedClose && !this.isReconnecting){
      this.isReconnecting = true;
      setTimeout(() => {
        // console.log('Attempting to reconnect...');
        this.liveChat_connect(chatId);  // Retry connection
      }, 10000);  // Wait 10 seconds before trying to reconnect  
    }
  }

  public liveChat_sendMessage(msg: string): void{
      if (this.liveSocket$ && this.isWebsocketConnected) {
        this.liveSocket$.send(msg);
      } else {
        // console.error('WebSocket is not connected. Cannot send message.');
      }
    }

    public liveChat_closeConnection(): void {
      // console.log('liveChat_closeConnection...');
      this.userInitiatedClose = true;
      this.isWebsocketConnected = false;
      if (this.liveSocket$) {
        this.liveSocket$.close();
      }
    }

  public onMessage(): Observable<string> {
     return this.messageSubject.asObservable();  // Provide an observable for external subscriptions
  }

  public isWSConnected(): boolean {
    return this.isWebsocketConnected;
  }

  getChats (): Observable<ChatMetadata[]>{
    const requestUrl = `${this.getChatsUrl}`;
    return this.http.get<ChatMetadata[]>(requestUrl); 
  }

  getStructuredProcessChat (processId: string, stageId: string, stepId: string): Observable<ChatMetadata | null>{
    const requestUrl = `${this.getStructuredProcessChatUrl}?processId=${processId}&stageId=${stageId}&stepId=${stepId}`;
    return this.http.get<ChatMetadata>(requestUrl); 
  }

  deleteChat (chatId: string): Observable<string>{
    const requestUrl = `${this.deleteChatUrl}?chatId=${chatId}`;
    return this.http.get<string>(requestUrl); 
  }

  // without limit returns the whole history, with limit - the newest page of messages before beforeMessageId, oldest first
  getOldMessages (chatId: string, limit?: number, beforeMessageId?: string) : Observable<ChatMessage[]>{
    let requestUrl = `${this.getChatMessagesUrl}?chatId=${chatId}`;
    if (limit) {
      requestUrl += `&limit=${limit}`;
    }
    if (beforeMessageId) {
      requestUrl += `&beforeMessageId=${encodeURIComponent(beforeMessageId)}`;
    }
    return this.http.get<ChatMessage[]>(requestUrl);
  }

  startNewChat (type : ChatTypeEnum = ChatTypeEnum.Default, baseObjId: string = "") : Observable<ChatMetadata>{
    return this.http.post<ChatMetadata>(this.startNewChatUrl, {chatType:type, baseObjId:baseObjId}, {});
  }

  startNewStructuredProcessChat (type: ChatTypeEnum, processId: string, stageId: string, stepId: string) : Observable<ChatMetadata>{
    return this.http.post<ChatMetadata>(this.startNewStructuredProcessChatUrl, {chatType:type, processId:processId, stageId:stageId, stepId:stepId}, {});
  }

  setUserMessage (message: string) : Observable<ServerResMessageData>{
    return this.http.post<ServerResMessageData>(this.setUserMessageUrl, { message: message}, {});
  }

  setMessageRating (messageId: string, messageRating : ChatMessageRatingEnum): Observable<string>{
    return this.http.post<string>(this.setMessageRatingUrl, {messageId : messageId, messageRating: messageRating});
  }

  getConversationalQuestion (questionId: string): Observable<EngagmentConversationQuestionData>{
    const requestUrl = `${this.getConversationalQuestionUrl}?questionId=${questionId}`;
    return this.http.get<EngagmentConversationQuestionData>(requestUrl); 
  }

  getUseCasesToChat (chatType: ChatTypeEnum): Observable<ExternalConverdationalUseCaseData[]>{
    const requestUrl = `${this.getUseCasesToChatUrl}?chatType=${chatType}`;
    return this.http.get<ExternalConverdationalUseCaseData[]>(requestUrl); 
  }

  registerChatForAudio (chatId: string): Observable<string>{
    const requestUrl = `${this.registerChatForAudioUrl}?chatId=${chatId}`;
    return this.http.put<string>(requestUrl, {}); 
  }

  unregisterChatForAudio (chatId: string): Observable<string>{
    const requestUrl = `${this.unregisterChatsForAudioUrl}`;
    return this.http.put<string>(requestUrl, {}); 
  }

  registerPeopleOfInterest (chatId: string, peopleOfInterest: string[]): Observable<string>{
    const requestUrl = `${this.registerPeopleOfInterestUrl}`;
    return this.http.post<string>(requestUrl, {chatId: chatId, poiList: peopleOfInterest}); 
  }
}
